import {
  EmptyState,
  FlexBox,
  Loader,
} from '@airship-platform/jam-web/components';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

const SUPPORT_MESSAGE = [
  'Sorry, there has been an error initializing the offline features.',
  'If you have other Airship tabs open, try clicking into the very first one you opened before retrying here — that one is often what caused this.',
  'Please click the "Refresh your browser" button to try again as this solves most issues.',
  'Click the "Reset" button when a refresh does not resolve the issue.',
  'If the issue persists, please contact support with the error message displayed.',
];

type RxDBSetupNotificationAlertProps = {
  entityName?: string;
  alertMessage?: string[];
  error: Error | null;
  resetDb?: () => Promise<void>;
};

const RxDBSetupNotificationAlert: React.FC<RxDBSetupNotificationAlertProps> = ({
  entityName,
  alertMessage,
  error,
  resetDb,
}) => {
  if (error) {
    return (
      <EmptyState
        title={`Failed to initialize offline ${entityName || 'database'}`}
        subtitle={SUPPORT_MESSAGE.map((msg, i) => (
          <Box key={i}>{msg}</Box>
        ))}
        icon={<Alert color="error" />}
        sx={{ maxWidth: 'md', margin: '2rem auto' }}
      >
        <FlexBox gap={2}>
          <Button color="green" onClick={() => window.location.reload()}>
            Refresh your browser
          </Button>
          {resetDb && (
            <Button color="red" onClick={resetDb}>
              Reset
            </Button>
          )}
        </FlexBox>

        <Divider flexItem />

        <Alert
          color="error"
          variant="outlined"
          icon={false}
          sx={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}
        >
          {`${error.message}`}
        </Alert>
      </EmptyState>
    );
  }

  const buildErrorMessage = (messages: string[]) => (
    <EmptyState
      title={`Please wait while we sync your data. Do not refresh this tab.`}
      subtitle={messages.map((msg, i) => (
        <Box key={i} sx={{ whiteSpace: 'pre-wrap' }}>
          {msg}
        </Box>
      ))}
      icon={<Alert color="warning" />}
      sx={{ maxWidth: 'md', margin: '2rem auto' }}
    />
  );

  const message = alertMessage?.length
    ? buildErrorMessage(alertMessage)
    : `Loading ${entityName || 'Airship'}...`;

  return <Loader message={message} isLoading={true} />;
};

export default RxDBSetupNotificationAlert;
